import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import './OverviewDashboard.css';
import SymbolSearch from './SymbolSearch';

function OverviewDashboard({ settings }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('ALL');
  const [symbolsList, setSymbolsList] = useState([]);

  const [activeTab, setActiveTab] = useState('realized');

  const [closedPositions, setClosedPositions] = useState([]);
  const [reportError, setReportError] = useState('');
  const realizedRequest = useRef(0);
  const [summaryStats, setSummaryStats] = useState(null);

  const [openPositions, setOpenPositions] = useState([]);
  const [openSummaryStats, setOpenSummaryStats] = useState(null);
  const [currentPrices, setCurrentPrices] = useState({});

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    if (transactions.length > 0) {
      if (activeTab === 'realized') {
        generateRealizedReport();
      } else {
        generateUnrealizedReport();
      }
    }
  }, [transactions, settings, startDate, endDate, selectedSymbol, activeTab]);

  // Refresh open-position valuations in the background without resetting UI.
  useEffect(() => {
    if (activeTab !== 'unrealized' || transactions.length === 0) return;
    const interval = setInterval(() => generateUnrealizedReport(), 30000);
    return () => clearInterval(interval);
  }, [activeTab, transactions, settings, startDate, endDate, selectedSymbol]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/transactions');

      // Filter out Future transactions but include Hami
      const nonFutureData = data.filter(t => t.MarketType !== 'Future');
      setTransactions(nonFutureData);
      const uniqueSymbols = [...new Set(nonFutureData.map(t => t.Symbol).filter(s => s !== 'حامی'))];
      setSymbolsList(uniqueSymbols);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
    setLoading(false);
  };

  const getCommissionRate = (marketType, transactionType) => {
    if (transactionType === 'bonus') return 0;
    const normalizedType = transactionType === 'short_sell' ? 'sell' : transactionType;
    const settingsMap = {};
    settings.forEach(s => { settingsMap[s.key] = s.value; });
    const key = `${marketType}_${normalizedType}_comm`.toLowerCase();

    // اگر تنظیمات وجود نداشت، از مقادیر پیش‌فرض استفاده شود
    const defaultRates = {
      'bourse_buy_comm': 0.003712,
      'bourse_sell_comm': 0.003712,
      'farabourse_buy_comm': 0.003712,
      'farabourse_sell_comm': 0.003712,
      'leveragedfund_buy_comm': 0.002345,
      'leveragedfund_sell_comm': 0.0023,
      'gold_buy_comm': 0.0011,
      'gold_sell_comm': 0.0011,
      'future_buy_comm': 0.0006,
      'future_sell_comm': 0.0006,
      'option_buy_comm': 0.00103,
      'option_sell_comm': 0.00103
    };

    return settingsMap[key] || defaultRates[key] || 0;
  };

  const getBankInterestRate = () => {
    const settingsMap = {};
    settings.forEach(s => { settingsMap[s.key] = s.value; });
    return settingsMap['bank_interest_rate'] || 0.20;
  };

  const fetchMarketPrice = async (symbol) => {
    try {
      const data = await api.get(`/api/market_prices/${encodeURIComponent(symbol)}`);
      if (data && data.price > 0) return data.price;
      return 0;
    } catch (error) {
      console.error('Error fetching price for', symbol, error);
      return 0;
    }
  };

  // ============================================================
  // FIXED: findAllClosedPositions
  // Accepts three closing patterns:
  //   1. short_sell + short_buy
  //   2. short_sell + sell  (NEW: legacy/user behavior support)
  //   3. buy + sell
  // ============================================================
  // ============================================================
  // FIXED: findOpenPositions
  // - Correctly identifies short positions closed by sell
  // - Date filters applied correctly
  // - Symbol filter applied correctly
  // ============================================================
  const findOpenPositions = () => {
    const processedSteps = new Set();

    let baseTxs = [...transactions];

    if (selectedSymbol !== 'ALL') {
      baseTxs = baseTxs.filter(t => t.Symbol === selectedSymbol);
    }

    const stepGroups = {};
    baseTxs.forEach(tx => {
      const key = `${tx.Symbol}_${tx.Step}`;
      if (!stepGroups[key]) stepGroups[key] = [];
      stepGroups[key].push(tx);
    });

    const openPositions = [];

    for (const [key, stepTxs] of Object.entries(stepGroups)) {
      if (processedSteps.has(key)) continue;
      processedSteps.add(key);

      const sorted = stepTxs.sort((a, b) => new Date(a.Date) - new Date(b.Date));
      const firstTx = sorted[0];

      const isShortPosition = sorted.some(t => t.Type === 'short_sell');
      const positionType = isShortPosition ? 'short' : 'long';

      let finalQty = 0;
      let totalBuyCost = 0;
      let totalSellRevenue = 0;
      let investedAmount = 0;
      let buyQty = 0;
      let sellQty = 0;
      let blockedAmount = 0;

      for (const tx of sorted) {
        const qty = parseFloat(tx.Quantity);
        const price = parseFloat(tx.Price);
        const comm = getCommissionRate(tx.MarketType, tx.Type);

        if (tx.Type === 'short_sell') {
          totalSellRevenue += qty * price * (1 - comm);
          finalQty += qty;
          sellQty += qty;
          blockedAmount += parseFloat(tx.BlockedAmount) || 0;

        } else if (tx.Type === 'short_buy') {
          // Closing a short via short_buy
          totalBuyCost += qty * price * (1 + comm);
          finalQty -= qty;
          buyQty += qty;

        } else if (tx.Type === 'sell' && isShortPosition) {
          // Closing a short via sell: we're BUYING to close, so we pay buy commission
          totalBuyCost += qty * price * (1 + comm);
          finalQty -= qty;
          buyQty += qty;
          sellQty += qty;

        } else if (tx.Type === 'buy' || tx.Type === 'bonus') {
          const cost = qty * price * (1 + comm);
          totalBuyCost += cost;
          finalQty += qty;
          buyQty += qty;
          investedAmount += cost;

        } else if (tx.Type === 'sell') {
          // Regular long position sell
          totalSellRevenue += qty * price * (1 - comm);
          finalQty -= qty;
          sellQty += qty;
        }
      }

      if (isShortPosition) {
        investedAmount = 0;
      }

      // Only include if there is remaining open quantity
      if (Math.abs(finalQty) > 0.01) {
        openPositions.push({
          symbol: firstTx.Symbol,
          step: firstTx.Step,
          positionType,
          startDate: firstTx.Date,
          remainingQty: Math.abs(finalQty),
          totalBuyCost,
          totalSellRevenue,
          investedAmount,
          buyQty,
          sellQty,
          blockedAmount,
          transactions: sorted
        });
      }
    }

    // FIXED: Apply date filters AFTER building open positions list
    // Filter is based on position startDate (date of first transaction)
    let result = openPositions;

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter(pos => new Date(pos.startDate) >= start);
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter(pos => new Date(pos.startDate) <= end);
    }

    return result;
  };

  // ============================================================
  // Report Generators
  // ============================================================
  const generateRealizedReport = async () => {
    const requestId = ++realizedRequest.current;
    setLoading(true); setReportError(''); setSummaryStats(null); setClosedPositions([]);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start', startDate);
      if (endDate) params.set('end', endDate);
      if (selectedSymbol !== 'ALL') params.set('symbol', selectedSymbol);
      const result = await api.get(`/api/portfolio-performance/realized?${params}`);
      if (requestId !== realizedRequest.current) return;
      setClosedPositions(result.positions); setSummaryStats(result.summary);
    } catch (error) {
      if (requestId === realizedRequest.current) setReportError(error.message || 'محاسبه گزارش انجام نشد');
    } finally { if (requestId === realizedRequest.current) setLoading(false); }
  };

  const generateUnrealizedReport = async () => {
    setLoading(true);

    const openPositionsList = findOpenPositions();

    const uniqueSymbols = [...new Set(openPositionsList.map(p => p.symbol))];
    const priceResults = await Promise.all(
      uniqueSymbols.map(async (symbol) => {
        const price = await fetchMarketPrice(symbol);
        return { symbol, price };
      })
    );

    const priceMap = {};
    priceResults.forEach(item => { priceMap[item.symbol] = item.price; });
    setCurrentPrices(priceMap);

    const positionsWithPnL = openPositionsList.map(pos => {
      const currentPrice = priceMap[pos.symbol] || 0;
      let unrealizedPnL = 0;
      let avgPrice = 0;

      if (pos.positionType === 'long') {
        avgPrice = pos.totalBuyCost / (pos.buyQty || 1);
        unrealizedPnL = (currentPrice - avgPrice) * pos.remainingQty;
      } else {
        avgPrice = pos.totalSellRevenue / (pos.sellQty || 1);
        unrealizedPnL = (avgPrice - currentPrice) * pos.remainingQty;
      }

      const today = new Date();
      const startDateObj = new Date(pos.startDate);
      const diffTime = Math.abs(today - startDateObj);
      const holdingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const bankRate = getBankInterestRate();
      // برای فروش فزاینده (short) هزینه فرصت محاسبه نمی‌شود
      const opportunityCost = pos.positionType === 'short' ? 0 : pos.investedAmount * bankRate * (holdingDays / 365);
      const netPnL = unrealizedPnL - opportunityCost;

      console.log(`[PORTFOLIO ${pos.symbol}] pos:`, {
        step: pos.step,
        positionType: pos.positionType,
        totalBuyCost: pos.totalBuyCost,
        totalSellRevenue: pos.totalSellRevenue,
        buyQty: pos.buyQty,
        sellQty: pos.sellQty,
        investedAmount: pos.investedAmount,
        remainingQty: pos.remainingQty,
        currentPrice: currentPrice,
        avgPrice: avgPrice,
        unrealizedPnL: unrealizedPnL,
        bankRate: bankRate,
        holdingDays: holdingDays,
        opportunityCost: opportunityCost,
        netPnL: netPnL
      });

      return {
        ...pos,
        currentPrice,
        avgPrice,
        unrealizedPnL,
        opportunityCost,
        netPnL,
        holdingDays,
        priceAvailable: currentPrice > 0
      };
    });

    let filteredPositions = positionsWithPnL;

    const openStats = calculateOpenSummaryStats(filteredPositions);
    setOpenPositions(filteredPositions);
    setOpenSummaryStats(openStats);
    setLoading(false);
  };

  // ============================================================
  // Stats Calculators
  // ============================================================
  const calculateOpenSummaryStats = (positions) => {
    if (positions.length === 0) {
      return {
        totalPositions: 0,
        totalInvested: 0,
        totalUnrealizedPnL: 0,
        totalOpportunityCost: 0,
        totalNetPnL: 0,
        winCount: 0,
        lossCount: 0
      };
    }

    let totalInvested = 0;
    let totalUnrealizedPnL = 0;
    let totalOpportunityCost = 0;
    let totalNetPnL = 0;
    let winCount = 0;
    let lossCount = 0;

    positions.forEach(pos => {
      totalInvested += pos.investedAmount;
      totalUnrealizedPnL += pos.unrealizedPnL;
      totalOpportunityCost += pos.opportunityCost;
      totalNetPnL += pos.netPnL;
      if (pos.unrealizedPnL > 0) winCount++;
      else if (pos.unrealizedPnL < 0) lossCount++;
    });

    return {
      totalPositions: positions.length,
      totalInvested,
      totalUnrealizedPnL,
      totalOpportunityCost,
      totalNetPnL,
      winCount,
      lossCount
    };
  };

  // ============================================================
  // Utility Helpers
  // ============================================================
  const formatPercent = (value) => {
    const formatted = Math.abs(value).toFixed(2);
    return value < 0 ? `-${formatted}%` : `${formatted}%`;
  };

  const formatCurrency = (value) => {
    return Math.round(value).toLocaleString();
  };

  const exportToExcel = () => {
    const dataToExport = activeTab === 'realized' ? closedPositions : openPositions;

    const excelData = dataToExport.map(item => {
      if (activeTab === 'realized') {
        return {
          'نماد': item.symbol,
          'پله': item.step,
          'نوع موقعیت': item.positionType === 'short' ? 'فروش فزاینده' : 'خرید',
          'تاریخ شروع': item.startDate,
          'تاریخ پایان': item.endDate,
          'مدت نگهداری (روز)': item.holdingDays,
          'سرمایه ثبت‌شده (ریال)': item.capitalKnown === false ? 'نامشخص' : formatCurrency(item.investedAmount),
          'سود/زیان موقعیت (ریال)': formatCurrency(item.positionProfit),
          'هزینه فرصت بانکی (ریال)': formatCurrency(item.opportunityCost),
          'سود/زیان خالص (ریال)': formatCurrency(item.netProfit)
        };
      } else {
        return {
          'نماد': item.symbol,
          'پله': item.step,
          'نوع موقعیت': item.positionType === 'short' ? 'فروش فزاینده' : 'خرید',
          'تاریخ شروع': item.startDate,
          'تعداد مانده': item.remainingQty,
          'قیمت فعلی': formatCurrency(item.currentPrice),
          'سرمایه ثبت‌شده (ریال)': item.capitalKnown === false ? 'نامشخص' : formatCurrency(item.investedAmount),
          'سود/زیان شناور (ریال)': formatCurrency(item.unrealizedPnL),
          'هزینه فرصت بانکی (ریال)': formatCurrency(item.opportunityCost),
          'سود/زیان خالص (ریال)': formatCurrency(item.netPnL)
        };
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    const sheetName = activeTab === 'realized' ? 'گزارش سود و زیان' : 'مانیتورینگ لحظه‌ای';
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${sheetName}.xlsx`);
  };

  // ============================================================
  // UI Renderers
  // ============================================================
  const renderTabs = () => (
    <div className="tabs-container">
      <button
        className={`tab-btn ${activeTab === 'realized' ? 'active' : ''}`}
        onClick={() => setActiveTab('realized')}
      >
        📊 سود و زیان تحقق‌یافته
      </button>
      <button
        className={`tab-btn ${activeTab === 'unrealized' ? 'active' : ''}`}
        onClick={() => setActiveTab('unrealized')}
      >
        📈 مانیتورینگ لحظه‌ای (موقعیت‌های باز)
      </button>
    </div>
  );

  const renderFilters = () => (
    <section className="filter-section" aria-label="فیلترهای گزارش پورتفوی">
      <div className="filter-heading">
        <div>
          <span className="filter-eyebrow">گزارش پورتفوی</span>
          <h3>فیلترهای گزارش</h3>
        </div>
        <button className="btn-clear" onClick={() => { setStartDate(''); setEndDate(''); setSelectedSymbol('ALL'); }}>
          پاک کردن فیلترها
        </button>
      </div>
      <div className="filter-row">
        <div className="filter-group date-filter-group">
          <label htmlFor="report-start-date">از تاریخ</label>
          <input id="report-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="filter-group date-filter-group">
          <label htmlFor="report-end-date">تا تاریخ</label>
          <input id="report-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div className="filter-group">
          <label htmlFor="report-symbol">نماد</label>
          <SymbolSearch id="report-symbol" symbols={symbolsList} value={selectedSymbol} onChange={setSelectedSymbol} />
        </div>
      </div>
      {activeTab === 'realized' && (
        <div className="filter-hint">
          ℹ️ تاریخ فیلتر بر اساس <strong>تاریخ فروش (بسته شدن موقعیت)</strong> اعمال می‌شود. همچنین سود حامی بر اساس <strong>تاریخ تراکنش</strong> در این بازه فیلتر می‌شود.
        </div>
      )}
      {activeTab === 'unrealized' && (
        <div className="filter-hint">
          ℹ️ تاریخ فیلتر بر اساس <strong>تاریخ شروع موقعیت</strong> اعمال می‌شود.
        </div>
      )}
    </section>
  );

  const renderRealizedSummaryCards = () => {
    if (!summaryStats || (summaryStats.totalPositions === 0 && !summaryStats.hamiProfit)) {
      return (
        <div className="info-box" style={{ margin: '20px 0' }}>
          ℹ️ در بازه زمانی انتخاب شده، هیچ موقعیت بسته‌ای یافت نشد.
        </div>
      );
    }

    const bankDiff = summaryStats.bankComparison;

    return (
      <div className="summary-cards">
        {/* Status Summary Card - RIGHT side in RTL (first in DOM) */}
        <div className="card breakdown-card status-summary-card">
          <div className="card-label">📋 خلاصه وضعیت</div>
          <div className="breakdown-content">
            <div className="breakdown-row">
              <div className="breakdown-info">
                <span className="breakdown-icon">📊</span>
                <span className="breakdown-label">تعداد موقعیت‌های بسته شده</span>
              </div>
              <div className="breakdown-value">
                {summaryStats.totalPositions}
                <span className="breakdown-sub">
                  🟢 برد: {summaryStats.winCount} | 🔴 باخت: {summaryStats.lossCount}
                </span>
                <span className="breakdown-sub win-rate">
                  نرخ برد: {formatPercent(summaryStats.winRate)}
                </span>
              </div>
            </div>
            <div className="breakdown-row">
              <div className="breakdown-info">
                <span className="breakdown-icon">💵</span>
                <span className="breakdown-label">{'جمع سرمایه خریدهای بسته‌شده'}</span>
              </div>
              <div className="breakdown-value">
                {formatCurrency(summaryStats.totalInvested)} ریال
              </div>
            </div>
            <div className="breakdown-row">
              <div className="breakdown-info">
                <span className="breakdown-icon">🏦</span>
                <span className="breakdown-label">هزینه فرصت بانکی</span>
              </div>
              <div className="breakdown-value opp-cost">
                {formatCurrency(summaryStats.totalOpportunityCost)} ریال
              </div>
            </div>
            <div className="breakdown-total">
              <div className="breakdown-info">
                <span className="breakdown-icon">✨</span>
                <span className="breakdown-label">{summaryStats.issueCount ? 'سود/زیان خالص معاملات معتبر' : 'سود/زیان خالص (پس از هزینه فرصت)'}</span>
              </div>
              <div className={`breakdown-value ${summaryStats.totalNetProfit < 0 ? 'negative' : 'positive'}`}>
                {formatCurrency(summaryStats.totalNetProfit)} ریال
              </div>
            </div>
            <div className="breakdown-comparison">
              {bankDiff == null ? 'درصد بازده قطعی: نیازمند بررسی اطلاعات معاملات' : `${Math.abs(bankDiff).toFixed(2)}% بازده خالص روی جمع سرمایه خریدها`}
            </div>
          </div>
        </div>

        {/* Profit Breakdown Card - LEFT side in RTL (second in DOM) */}
        <div className="card breakdown-card">
          <div className="card-label">📊 تفکیک سود/زیان کل</div>
          <div className="breakdown-content">
            <div className="breakdown-row">
              <div className="breakdown-info">
                <span className="breakdown-icon">💰</span>
                <span className="breakdown-label">سود حامی</span>
              </div>
              <div className={`breakdown-value ${summaryStats.hamiProfit >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(summaryStats.hamiProfit)} ریال
              </div>
            </div>
            <div className="breakdown-row">
              <div className="breakdown-info">
                <span className="breakdown-icon">📈</span>
                <span className="breakdown-label">سایر معاملات</span>
              </div>
              <div className={`breakdown-value ${summaryStats.regularProfit < 0 ? 'negative' : 'positive'}`}>
                {formatCurrency(summaryStats.regularProfit)} ریال
              </div>
            </div>
            <div className="breakdown-total">
              <div className="breakdown-info">
                <span className="breakdown-icon">🔢</span>
                <span className="breakdown-label">جمع کل</span>
              </div>
              <div className={`breakdown-value ${summaryStats.totalProfit < 0 ? 'negative' : 'positive'}`}>
                {formatCurrency(summaryStats.totalProfit)} ریال
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUnrealizedSummaryCards = () => {
    if (!openSummaryStats || openSummaryStats.totalPositions === 0) {
      return (
        <div className="info-box" style={{ margin: '20px 0' }}>
          ℹ️ در حال حاضر هیچ موقعیت بازی ندارید.
        </div>
      );
    }

    return (
      <div className="summary-cards">
        <div className="card">
          <div className="card-label">تعداد موقعیت‌های باز</div>
          <div className="card-value">{openSummaryStats.totalPositions}</div>
          <div className="card-sub">
            🟢 سود شناور: {openSummaryStats.winCount} | 🔴 زیان شناور: {openSummaryStats.lossCount}
          </div>
        </div>
        <div className="card">
          <div className="card-label">کل سرمایه درگیر</div>
          <div className="card-value">{formatCurrency(openSummaryStats.totalInvested)} ریال</div>
        </div>
        <div className="card">
          <div className="card-label">سود/زیان شناور کل</div>
          <div className={`card-value ${openSummaryStats.totalUnrealizedPnL < 0 ? 'negative' : 'positive'}`}>
            {formatCurrency(openSummaryStats.totalUnrealizedPnL)} ریال
          </div>
        </div>
        <div className="card">
          <div className="card-label">هزینه فرصت تا امروز</div>
          <div className="card-value opp-cost">{formatCurrency(openSummaryStats.totalOpportunityCost)} ریال</div>
        </div>
        <div className="card highlight">
          <div className="card-label">سود/زیان خالص شناور</div>
          <div className={`card-value ${openSummaryStats.totalNetPnL < 0 ? 'negative' : 'positive'}`}>
            {formatCurrency(openSummaryStats.totalNetPnL)} ریال
          </div>
        </div>
      </div>
    );
  };

  const renderPositionsTable = () => {
    if (closedPositions.length === 0) return null;

    return (
      <div className="details-section">
        <div className="details-header">
          <h3>📋 لیست موقعیت‌های بسته شده</h3>
          <button className="btn-export" onClick={exportToExcel}>📥 دانلود اکسل</button>
        </div>
        <div className="table-container">
          <table className="styled-table">
            <thead>
              <tr>
                <th>نماد</th>
                <th>پله</th>
                <th>نوع</th>
                <th>مدت (روز)</th>
                <th>سرمایه درگیر</th>
                <th>سود/زیان</th>
                <th>هزینه فرصت</th>
                <th>سود خالص</th>
              </tr>
            </thead>
            <tbody>
              {closedPositions.map((item, index) => (
                <tr key={index}>
                  <td><strong>{item.symbol}</strong></td>
                  <td>{item.step}</td>
                  <td>
                    <span className={`badge ${item.positionType === 'short' ? 'badge-short' : 'badge-long'}`}>
                      {item.positionType === 'short' ? '📉 فروش' : '📈 خرید'}
                    </span>
                  </td>
                  <td>{item.holdingDays}</td>
                  <td>{item.capitalKnown === false ? 'نامشخص' : formatCurrency(item.investedAmount)}</td>
                  <td className={item.positionProfit < 0 ? 'negative' : 'positive'}>
                    {formatCurrency(item.positionProfit)}
                  </td>
                  <td className="opp-cost">{formatCurrency(item.opportunityCost)}</td>
                  <td className={item.netProfit < 0 ? 'negative' : 'positive'}>
                    {formatCurrency(item.netProfit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderOpenPositionsTable = () => {
    if (openPositions.length === 0) return null;

    return (
      <div className="details-section">
        <div className="details-header">
          <h3>📋 لیست موقعیت‌های باز - مانیتورینگ لحظه‌ای</h3>
          <button className="btn-export" onClick={exportToExcel}>📥 دانلود اکسل</button>
        </div>
        <div className="table-container">
          <table className="styled-table">
            <thead>
              <tr>
                <th>نماد</th>
                <th>پله</th>
                <th>نوع</th>
                <th>تعداد مانده</th>
                <th>قیمت فعلی</th>
                <th>سرمایه درگیر</th>
                <th>سود/زیان شناور</th>
                <th>هزینه فرصت</th>
                <th>سود خالص شناور</th>
              </tr>
            </thead>
            <tbody>
              {openPositions.map((item, index) => (
                <tr key={index}>
                  <td><strong>{item.symbol}</strong></td>
                  <td>{item.step}</td>
                  <td>
                    <span className={`badge ${item.positionType === 'short' ? 'badge-short' : 'badge-long'}`}>
                      {item.positionType === 'short' ? '📉 فروش' : '📈 خرید'}
                    </span>
                  </td>
                  <td>{item.remainingQty}</td>
                  <td>{item.priceAvailable ? formatCurrency(item.currentPrice) : '⏳ درحال دریافت...'}</td>
                  <td>{item.capitalKnown === false ? 'نامشخص' : formatCurrency(item.investedAmount)}</td>
                  <td className={item.unrealizedPnL < 0 ? 'negative' : 'positive'}>
                    {formatCurrency(item.unrealizedPnL)}
                  </td>
                  <td className="opp-cost">{formatCurrency(item.opportunityCost)}</td>
                  <td className={item.netPnL < 0 ? 'negative' : 'positive'}>
                    {formatCurrency(item.netPnL)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAnalyticalReport = () => {
    if (!summaryStats || summaryStats.totalPositions === 0) return null;

    const bankDiff = summaryStats.bankComparison;

    return (
      <div className="analytical-report-section">
        <h3>📝 گزارش تحلیلی عملکرد</h3>
        <div className="report-grid">
          <div className="report-item">
            <span className="report-label">تعداد کل معاملات بسته شده:</span>
            <span className="report-value">{summaryStats.totalPositions}</span>
          </div>
          <div className="report-item">
            <span className="report-label">میانگین سود هر معامله:</span>
            <span className={`report-value ${summaryStats.totalProfit / summaryStats.totalPositions < 0 ? 'negative' : 'positive'}`}>
              {formatCurrency(summaryStats.totalProfit / summaryStats.totalPositions)} ریال
            </span>
          </div>
          <div className="report-item">
            <span className="report-label">نرخ موفقیت (برد):</span>
            <span className="report-value">{formatPercent(summaryStats.winRate)}</span>
          </div>
          <div className="report-item">
            <span className="report-label">بازدهی کل سرمایه:</span>
            <span className={`report-value ${summaryStats.profitPercent < 0 ? 'negative' : 'positive'}`}>
              {summaryStats.profitPercent == null ? 'قابل محاسبه قطعی نیست' : formatPercent(summaryStats.profitPercent)}
            </span>
          </div>
        </div>
        <div className="comparison-box">
          <h4>{bankDiff == null ? 'بازده درصدی تا رفع خطاهای معاملات قطعی نیست.' : `بازده خالص روی جمع سرمایه خریدها: ${formatPercent(bankDiff)}`}</h4>
          <p>سود/زیان خالص {formatCurrency(summaryStats.totalNetProfit)} ریال، پس از کسر {formatCurrency(summaryStats.totalOpportunityCost)} ریال هزینه فرصت خریدها محاسبه شده است. هزینه فرصت فروش فزاینده صفر است.</p>
          <p>جمع سرمایه خریدها، گردش تجمعی هزینه خرید است؛ فروش فزاینده در این شاخص سرمایه صفر دارد.</p>
        </div>
      </div>
    );
  };

  // ============================================================
  // Main Render
  // ============================================================
  if (transactions.length === 0 && !loading) {
    return (
      <div className="overview-dashboard">
        <h1>📊 داشبورد کلان</h1>
        <div className="info-box">هیچ داده‌ای برای نمایش وجود ندارد. لطفاً ابتدا معاملات خود را ثبت کنید.</div>
      </div>
    );
  }

  return (
    <div className="overview-dashboard">
      <h1>📊 داشبورد کلان</h1>

      {renderTabs()}
      {renderFilters()}
      {activeTab === 'realized' && reportError && <div className="calculation-warning" role="alert">{reportError}</div>}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>در حال پردازش داده‌ها...</p>
        </div>
      ) : (
        <>
          {activeTab === 'realized' ? (
            <>
              {renderRealizedSummaryCards()}
              {renderPositionsTable()}
              {renderAnalyticalReport()}
            </>
          ) : (
            <>
              {renderUnrealizedSummaryCards()}
              {renderOpenPositionsTable()}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default OverviewDashboard;
